import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError, api } from '@/api/client'
import { applyLocale, resolveLocale } from '@/i18n'
import type { CurrentTenant, CurrentUser, Session, TwoFactorRequired } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const tenant = ref<CurrentTenant | null>(null)
  const loading = ref(false)

  /** False until the first session probe finishes, so the router can wait instead of guessing. */
  const initialized = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() =>
    (user.value?.roles ?? []).some((role) => role === 'Owner' || role === 'Admin'),
  )
  const isOwner = computed(() => (user.value?.roles ?? []).includes('Owner'))

  // Not derived from roles, because it is not one: it is configuration, above every workspace.
  // The server answers the same question again on every request behind it, so this only decides
  // whether the console is worth putting in the menu.
  const isPlatformAdmin = computed(() => user.value?.isPlatformAdmin === true)

  function apply(session: Session) {
    user.value = session.user
    tenant.value = session.tenant

    // Signing in can change the language: this person may read English in a Danish workspace.
    // Not awaited — the workspace's own wording is a refinement, and holding sign-in open on a
    // second round trip to learn that a shop calls customers something else is the wrong trade.
    void applyLocale(resolveLocale(session.user.locale, session.tenant.locale))
  }

  function clear() {
    user.value = null
    tenant.value = null
  }

  /** Probes the session cookie. A 401 here is the normal "not signed in" answer, not an error. */
  async function restore(): Promise<void> {
    if (initialized.value) return
    loading.value = true
    try {
      apply(await api.get<Session>('/api/auth/session'))
    } catch (error) {
      clear()
      if (!(error instanceof ApiError) || !error.isUnauthorized) {
        console.warn('Session probe failed', error)
      }
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  /**
   * Signs in, or reports that the password was right and a code is on its way.
   *
   * Answers `null` once the session is established and a `TwoFactorRequired` when it is not —
   * because at that point the sign-in is deliberately unfinished: the server holds a short-lived
   * partial cookie and nothing here is signed in yet. Returned rather than thrown, so "you need a
   * code" stays out of the error path, where it would read as a failure.
   */
  async function login(
    email: string,
    password: string,
    rememberMe: boolean,
  ): Promise<TwoFactorRequired | null> {
    loading.value = true
    try {
      const result = await api.post<Session | TwoFactorRequired>(
        '/api/auth/login',
        { email, password, rememberMe },
      )

      if ('maskedEmail' in result) return result

      apply(result)
      return null
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  /** The second step. The partial cookie the server set is what identifies the caller. */
  async function completeTwoFactor(code: string, rememberMe: boolean, rememberBrowser: boolean) {
    loading.value = true
    try {
      apply(await api.post<Session>('/api/auth/two-factor', { code, rememberMe, rememberBrowser }))
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  /** Answers with a fresh session, so the store stays the single copy of what is on. */
  async function setTwoFactor(enabled: boolean) {
    apply(await api.put<Session>('/api/auth/two-factor', { enabled }))
  }

  async function logout() {
    try {
      await api.post('/api/auth/logout')
    } finally {
      clear()
    }
  }

  return {
    user,
    tenant,
    loading,
    initialized,
    isAuthenticated,
    isAdmin,
    isOwner,
    isPlatformAdmin,
    restore,
    login,
    completeTwoFactor,
    setTwoFactor,
    logout,
  }
})
